import { Link } from "react-router-dom";
import { ArrowUpRight, Home as HomeIcon, Wrench, ClipboardCheck, TrendingUp, Layers } from "lucide-react";
import { useT } from "@/context/ContentContext";
import PageHead from "@/components/PageHead";

const AREAS = [
    {
        n: "01", icon: HomeIcon, title: "Remodelações",
        desc: "Remodelações integrais ou parciais de apartamentos, moradias e outros imóveis. Da preparação à entrega, asseguramos a execução e coordenação das diferentes especialidades necessárias.",
        items: ["Remodelações integrais", "Remodelações parciais", "Cozinhas", "Casas de banho", "Alteração e renovação de espaços", "Renovação de acabamentos"],
        img: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        n: "02", icon: ClipboardCheck, title: "Gestão de Obra",
        desc: "Para quem já tem os empreiteiros ou prefere não perder tempo com coordenação. Assumimos a organização, fiscalização e gestão da obra — mantém-se com um único interlocutor que assegura a correta execução, o controlo dos prazos e a comunicação com todas as partes envolvidas — começando por si.",
        items: [
            "Coordenação de empreiteiros e especialidades",
            "Fiscalização técnica e controlo de qualidade",
            "Gestão de prazos e cronograma",
            "Comunicação centralizada — um só interlocutor",
            "Verificação de conformidade e acabamentos",
            "Atualizações com fotografias e resumo semanal",
            "Resolução de imprevistos durante a obra",
        ],
        img: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
    {
        n: "03", icon: TrendingUp, title: "Valorização de Imóveis",
        desc: "Intervenções pensadas para melhorar o estado, funcionalidade, conforto, apresentação e potencial de um imóvel — para habitação, arrendamento ou venda.",
        items: ["Habitação própria", "Imóveis para venda", "Imóveis para arrendamento", "Imóveis entre arrendamentos", "Património que necessite de atualização", "Renovação estética ou funcional"],
        img: "https://images.unsplash.com/photo-1738168279272-c08d6dd22002?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    },
];

const ESPECIALIDADES = [
    "Demolições", "Alvenarias", "Rebocos e Estuques", "Gesso Cartonado / Pladur",
    "Pinturas", "Pavimentos", "Revestimentos", "Impermeabilizações",
    "Isolamentos Térmicos e Acústicos", "Canalização", "Eletricidade",
    "Climatização e Ar Condicionado", "Ventilação", "Carpintaria", "Serralharia",
    "Caixilharia", "Vidros", "Estores", "Coberturas e Telhados", "Fachadas",
    "Cozinhas", "Casas de Banho", "Microcimento e Revestimentos Contínuos",
];

export default function Servicos() {
    const t = useT();
    return (
        <>
            <PageHead
                title="Serviços — Remodelações, Gestão de Obra, Valorização | O Homem da Obra"
                description="Três áreas coordenadas como uma obra integrada: Remodelações completas, Gestão de Obra (fiscalização e coordenação com os seus empreiteiros) e Valorização de imóveis. Um interlocutor, um método, orçamentos claros."
                canonical="https://ohomemdaobra.pt/servicos"
            />
            <section className="pt-32 pb-8">
                <div className="max-w-4xl mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><Layers className="w-3.5 h-3.5" /> {t("servicos.eyebrow", "Serviços")}</div>
                    <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
                        {t("servicos.title", "Três áreas. Um método")}<span className="text-[hsl(var(--copper))]">.</span>
                    </h1>
                    <p className="text-[hsl(var(--graphite))]/75 text-lg mt-8 max-w-2xl leading-relaxed">
                        {t("servicos.subtitle", "Remodelações, gestão de obra e valorização de imóveis — coordenados como uma obra integrada, com um único interlocutor.")}
                    </p>
                </div>
            </section>

            <section className="pb-16 sm:pb-24">
                <div className="max-w-6xl mx-auto px-6 sm:px-10 space-y-6">
                    {AREAS.map((a, i) => (
                        <div key={a.n} className={`card-stone p-4 sm:p-6 grid md:grid-cols-2 gap-6 items-center ${i % 2 ? "md:[&>div:first-child]:order-2" : ""}`} data-testid={`area-${a.n}`}>
                            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                                <img src={a.img} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="px-2 sm:px-4">
                                <div className="flex items-baseline gap-3 mb-3">
                                    <span className="font-mono text-sm text-[hsl(var(--copper))]">{a.n}</span>
                                    <h2 className="font-display text-3xl sm:text-4xl font-bold">{a.title}<span className="text-[hsl(var(--copper))]">.</span></h2>
                                </div>
                                <p className="text-[hsl(var(--graphite))]/75 leading-relaxed mb-5">{a.desc}</p>
                                <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm text-[hsl(var(--graphite))]/80">
                                    {a.items.map((it) => (
                                        <li key={it} className="flex items-center gap-2 before:content-['—'] before:text-[hsl(var(--copper))]">{it}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-6 sm:px-10">
                    <div className="eyebrow mb-6"><Wrench className="w-3.5 h-3.5" /> Especialidades</div>
                    <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl">
                        {t("servicos.esp.title", "Uma obra. Todas as especialidades.")}
                    </h2>
                    <p className="text-[hsl(var(--graphite))]/70 text-lg mt-6 max-w-2xl">
                        {t("servicos.esp.subtitle", "Uma remodelação raramente envolve apenas uma especialidade. Asseguramos e coordenamos os diferentes trabalhos necessários — para que não tenha de gerir vários profissionais, fornecedores e intervenções separadamente.")}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-10">
                        {ESPECIALIDADES.map((e) => (
                            <span key={e} className="px-4 py-2 bg-white rounded-full text-sm font-medium !text-[hsl(var(--forest))] border border-[hsl(var(--forest))]/25 hover:bg-[hsl(var(--forest))] hover:!text-white transition cursor-default">{e}</span>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="bg-[hsl(var(--forest))] rounded-[2rem] p-10 sm:p-16 text-center text-white">
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
                            {t("servicos.cta.title", "Está a pensar iniciar uma obra?")}
                        </h2>
                        <p className="text-white/80 text-lg mt-6 max-w-xl mx-auto">
                            {t("servicos.cta.subtitle", "Comece por nos contar o que pretende fazer. Nós tratamos do resto — e mantemo-lo a par de tudo.")}
                        </p>
                        <Link to="/orcamento" className="btn-copper mt-8">
                            Fale-nos da sua obra
                            <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
